import React from "react";
import { Users, Target, Search, Loader2, Smartphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface UserGroup {
  id: number;
  name: string;
  description: string;
  member_count: number;
  created_at: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  avatarUrl: string;
  isActive: boolean;
  registeredAt: string;
}

interface GroupAudienceCount {
  totalMembers: number;
  withTokens: number;
  loading: boolean;
}

interface TargetAudienceSelectorProps {
  userGroups: UserGroup[];
  selectedUserGroup: UserGroup | null;
  selectedUsers: User[];
  selectedTopic: string | null;
  userSearchQuery: string;
  searchingUsers: boolean;
  userSearchResults: User[];
  groupAudienceCount?: GroupAudienceCount;
  topicSubscriberCounts?: Record<string, number>;
  sendMode?: "immediate" | "scheduled" | "draft";
  onUserGroupChange: (groupId: string) => void;
  onUserSearchChange: (query: string) => void;
  onToggleUser: (user: User) => void;
  onTopicChange: (topic: string) => void;
  onTabChange?: (tab: string) => void;
}

export default function TargetAudienceSelector({
  userGroups,
  selectedUserGroup,
  selectedUsers,
  selectedTopic,
  userSearchQuery,
  searchingUsers,
  userSearchResults,
  groupAudienceCount,
  topicSubscriberCounts,
  sendMode,
  onUserGroupChange,
  onUserSearchChange,
  onToggleUser,
  onTopicChange,
  onTabChange,
}: TargetAudienceSelectorProps) {
  const formatTopicCount = (topic: string) => {
    if (!topicSubscriberCounts) return null;
    const count = topicSubscriberCounts[topic];
    if (count === undefined) return null;
    return `(${count.toLocaleString()} subscriber${count !== 1 ? 's' : ''})`;
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-4 w-4" />
          Target Audience
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sendMode === "scheduled" && (
          <Alert className="mb-3 py-2 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <AlertDescription className="text-xs text-amber-700 dark:text-amber-300">
              Scheduled campaigns require a user group selection.
            </AlertDescription>
          </Alert>
        )}
        <Tabs defaultValue="group" className="w-full" onValueChange={onTabChange}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="group">User Group</TabsTrigger>
            <TabsTrigger value="individual">Individual Users</TabsTrigger>
            <TabsTrigger value="topics">Topic</TabsTrigger>
          </TabsList>

          <TabsContent value="group" className="space-y-4">
            <div>
              <Label>Select User Group</Label>
              <Select
                value={selectedUserGroup?.id?.toString() || ""}
                onValueChange={onUserGroupChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user group" />
                </SelectTrigger>
                <SelectContent>
                  {userGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id.toString()}>
                      <div className="flex items-center justify-between w-full">
                        <span>{group.name}</span>
                        <Badge variant="outline">
                          {group.member_count} users
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedUserGroup && (
              <Alert>
                <Users className="h-4 w-4" />
                <AlertDescription>
                  {groupAudienceCount?.loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Calculating audience for "{selectedUserGroup.name}"...
                    </span>
                  ) : groupAudienceCount && groupAudienceCount.totalMembers > 0 ? (
                    <div className="space-y-1">
                      <div>
                        <strong>{groupAudienceCount.totalMembers} members</strong> in "{selectedUserGroup.name}"
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Smartphone className="h-3 w-3" />
                        <strong>{groupAudienceCount.withTokens}</strong> reachable (have push tokens)
                        {groupAudienceCount.totalMembers - groupAudienceCount.withTokens > 0 && (
                          <span>
                            {" "}— {groupAudienceCount.totalMembers - groupAudienceCount.withTokens} without tokens
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span>
                      Will send to{" "}
                      <strong>{selectedUserGroup.member_count} users</strong> in "{selectedUserGroup.name}"
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="individual" className="space-y-4">
            <div>
              <Label>Search Users</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email (minimum 2 characters)..."
                  value={userSearchQuery}
                  onChange={(e) => onUserSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Type at least 2 characters to search for users
              </p>
            </div>

            <div>
              <Label>Select Individual Users</Label>
              <div className="max-h-60 overflow-y-auto border rounded-md p-2">
                {searchingUsers ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto mb-2"></div>
                    Searching users...
                  </div>
                ) : userSearchQuery.trim().length < 2 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="mx-auto h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">Start typing to search for users</p>
                    <p className="text-xs">Search by name or email address</p>
                  </div>
                ) : userSearchResults.length > 0 ? (
                  userSearchResults.map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted ${
                        selectedUsers.some((u) => u.id === user.id)
                          ? "bg-primary/10"
                          : ""
                      }`}
                      onClick={() => onToggleUser(user)}
                    >
                      <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                      </div>
                      <Badge variant="outline">
                        {user.isActive ? "active" : "inactive"}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="mx-auto h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No users found</p>
                    <p className="text-xs">Try a different search term</p>
                  </div>
                )}
              </div>
            </div>

            {selectedUsers.length > 0 && (
              <Alert>
                <Target className="h-4 w-4" />
                <AlertDescription>
                  Selected <strong>{selectedUsers.length} users</strong>
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="topics" className="space-y-4">
            <RadioGroup onValueChange={onTopicChange} value={selectedTopic || undefined}>
              {[
                { value: "all_users", label: "All Users" },
                { value: "android", label: "Android" },
                { value: "ios", label: "iOS" },
                { value: "new_summary", label: "New Summary" },
                { value: "trending_now", label: "Trending Now" },
                { value: "daily_quote", label: "Daily Quote" },
              ].map(({ value, label }) => (
                <div key={value} className="flex items-center gap-2">
                  <RadioGroupItem value={value} id={value} />
                  <label className="Label" htmlFor={value}>
                    {label}
                    {formatTopicCount(value) && (
                      <span className="ml-1 text-xs text-muted-foreground font-normal">
                        {formatTopicCount(value)}
                      </span>
                    )}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
